// MediaOffset tracks the EXT-X-BOXCAST-OFFSET tag from the HLS playlist so that
// absolute media times (relative to the beginning of the streamed content, as stored
// on video markers) can be translated to positions on the player timeline and back.
// The translation is non-trivial when a recording has been trimmed (the playlist no
// longer starts at the beginning of the content) or on a live broadcast once the
// sliding window has dropped earlier segments.
//
// NOTE: hlsjs-playback destroys and recreates its hls.js instance on every load/stop,
// so a listener bound once would silently go stale. sync() re-binds to the playback's
// current instance and is called from every translation, making the tracker
// self-healing; seeding from the already-loaded level details covers playlists that
// loaded while we weren't listening.
//
// For playbacks without hls.js (e.g. native HLS in Safari) the translation stays an
// identity, matching the previous behavior.

const DOUBLE_LEVEL_GUARD_MS = 2000;

function findBoxcastOffset(tagList) {
  if (!tagList) {
    return undefined;
  }
  for (var i = 0; i < tagList.length; i++) {
    if (tagList[i][0] == 'EXT-X-BOXCAST-OFFSET') {
      return parseFloat(tagList[i][1]);
    }
  }
  return undefined;
}

export default class MediaOffset {
  constructor() {
    this._mediaOffset = 0;
    // wall-clock time (Date.now()) at which _mediaOffset was captured, or null if it
    // never has been. used to extrapolate the offset between playlist reloads, since it
    // otherwise reflects a stale, once-per-reload snapshot while the live window keeps
    // trimming content in real time.
    this._mediaOffsetCapturedAt = null;
    this._avoidDoubleLevel = false;
    this._guardTimer = null;
    this._playback = null;
    this._hls = null;
    this._changeHandlers = [];
    this._onLevelLoaded = this._onLevelLoaded.bind(this);
  }

  // start tracking the given playback, detaching from any previous one. a no-op for
  // playbacks without hls.js.
  attachToPlayback(playback) {
    this.detach();
    this._playback = playback || null;
    this.sync();
  }

  detach() {
    if (this._hls) {
      this._hls.off('hlsLevelLoaded', this._onLevelLoaded);
      this._hls = null;
    }
    this._playback = null;
    clearTimeout(this._guardTimer);
    this._avoidDoubleLevel = false;
  }

  // move our listener to the playback's current hls.js instance if it has been
  // recreated since we last looked (cheap when nothing changed).
  sync() {
    var currentHls = (this._playback && this._playback._hls) || null;
    if (currentHls === this._hls) {
      return;
    }
    if (this._hls) {
      this._hls.off('hlsLevelLoaded', this._onLevelLoaded);
    }
    this._hls = currentHls;
    if (this._hls) {
      this._hls.on('hlsLevelLoaded', this._onLevelLoaded);
      this._seedFromCurrentLevel();
    }
  }

  // register a handler to be called whenever the offset changes (e.g. once the first
  // playlist loads, or as a live window slides).
  onChange(handler) {
    this._changeHandlers.push(handler);
  }

  // translate an absolute media time to a seekable position on the player timeline.
  toPlayerTime(absoluteTime) {
    this.sync();
    return absoluteTime - this._estimatedMediaOffset();
  }

  // translate a position on the player timeline to an absolute media time.
  toAbsoluteTime(playerTime) {
    this.sync();
    return playerTime + this._estimatedMediaOffset();
  }

  // the offset only advances once per playlist reload, but a live sliding window keeps
  // trimming content in real time between reloads, so extrapolate using wall-clock time
  // elapsed since the last capture. this only holds while (a) there's actually
  // something trimmed to extrapolate, and (b) playback is still live: a VOD
  // recording's trim amount is permanently fixed (it doesn't grow just because the
  // viewer has had the page open a while), and a live broadcast still within the
  // window has an offset of 0 that isn't growing yet either.
  _estimatedMediaOffset() {
    if (this._mediaOffsetCapturedAt === null || this._mediaOffset === 0) {
      return this._mediaOffset;
    }
    if (!this._isCurrentPlaybackLive()) {
      return this._mediaOffset;
    }
    return this._mediaOffset + (Date.now() - this._mediaOffsetCapturedAt) / 1000;
  }

  // whether the current playback is presently live, vs. a VOD recording.
  _isCurrentPlaybackLive() {
    return (
      !!this._playback &&
      typeof this._playback.getPlaybackType === 'function' &&
      this._playback.getPlaybackType() === 'live'
    );
  }

  // capture a playlist that had already loaded before we (re)attached.
  _seedFromCurrentLevel() {
    var levels = this._hls.levels || [];
    var level = this._hls.currentLevel >= 0 ? levels[this._hls.currentLevel] : null;
    if (!level || !level.details) {
      level = levels.filter((l) => l && l.details)[0];
    }
    if (level && level.details) {
      this._capture(level.details);
    }
  }

  _onLevelLoaded(evt, levelDetails) {
    // XXX: there's an odd behavior in hls.js where it fires 2 levelLoaded events in
    // quick succession; the first is the one we want because the 2nd has a mutated
    // "start" to be the startPTS.  so ignore the subsequent one but keep the first.
    if (this._avoidDoubleLevel) {
      return;
    }
    this._avoidDoubleLevel = true;
    this._guardTimer = setTimeout(() => (this._avoidDoubleLevel = false), DOUBLE_LEVEL_GUARD_MS);

    this._capture(levelDetails.details);
  }

  _capture(details) {
    var firstFragment = details.fragments && details.fragments[0];
    // the tag most commonly lands on the first fragment's init segment, but check the
    // other places hls.js may attach it.
    const tagListCandidates = [
      details.initSegment && details.initSegment.tagList,
      firstFragment && firstFragment.tagList,
      firstFragment && firstFragment.initSegment && firstFragment.initSegment.tagList,
    ].filter(Boolean);
    let mediaOffset;
    for (const tagList of tagListCandidates) {
      mediaOffset = findBoxcastOffset(tagList);
      if (mediaOffset !== undefined) break;
    }

    // NOTE: we deliberately don't factor in the first fragment's `start` (its position
    // on hls.js's own internal timeline). that value is only continuity-preserved
    // across playlist reloads once a fragment has actually been downloaded/demuxed
    // (see hls.js's mergeDetails, which propagates a real PTS-derived start only when
    // one is already known). a live-edge viewer never downloads the window's oldest
    // fragment, so its `start` is just whatever the raw parser assigned it in that one
    // parse (accumulated EXTINF durations from 0) — a per-reload artifact, not a
    // stable quantity — so folding it in does more harm than good.
    mediaOffset = mediaOffset || 0;
    const changed = mediaOffset !== this._mediaOffset;
    this._mediaOffset = mediaOffset;
    // always refresh the capture time, even when the value is unchanged, so the
    // extrapolation baseline stays as fresh as possible.
    this._mediaOffsetCapturedAt = Date.now();
    if (changed) {
      this._changeHandlers.forEach((handler) => handler());
    }
  }
}
